import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, MobileStepper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGuide } from '../context/GuideContext';
import { useNavigate } from 'react-router-dom';

const PRIMARY = '#4d4cac';
const SIDEBAR_WIDTH = 220;
const CARD_WIDTH = 290;

const StartGuide = () => {
  const { active, currentStep, stepIndex, totalSteps, nextStep, prevStep, dismiss } = useGuide();
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();

  // Navigate automatically when a step requires it
  useEffect(() => {
    if (currentStep?.navigateTo) {
      navigate(currentStep.navigateTo);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id]);

  // Measure the target element position, with retry for elements that appear after navigation
  useEffect(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      return;
    }

    let retries = 0;
    const measure = () => {
      const el = document.querySelector(`[data-guide-id="${currentStep.target}"]`);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      } else if (retries < 8) {
        retries++;
        setTimeout(measure, 200);
      }
    };

    // Initial delay to allow sidebar section to expand / page to render
    const timer = setTimeout(measure, 150);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
    };
  }, [currentStep?.id, currentStep?.target]);

  if (!active || !currentStep) return null;

  const { tooltipSide } = currentStep;
  const hasTarget = !!currentStep.target;
  const isLast = stepIndex === totalSteps - 1;
  const isFirst = stepIndex === 0;

  // --- Tooltip card position ---
  let cardStyle = {};
  let arrowEl = null;

  if (hasTarget && targetRect) {
    if (tooltipSide === 'left') {
      // Sidebar item: card sits to the LEFT of the sidebar, vertically centered on element
      const top = Math.max(12, Math.min(
        targetRect.top + targetRect.height / 2 - 115,
        window.innerHeight - 250
      ));
      cardStyle = { right: SIDEBAR_WIDTH + 18, top };
      // Arrow pointing right → toward the sidebar
      arrowEl = (
        <Box sx={{
          position: 'absolute',
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '10px solid transparent',
          borderBottom: '10px solid transparent',
          borderLeft: '12px solid #fff',
          filter: 'drop-shadow(2px 0 2px rgba(0,0,0,0.08))',
        }} />
      );
    } else if (tooltipSide === 'bottom') {
      // Page element: card sits BELOW the target button
      const left = Math.max(8, Math.min(
        targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2,
        window.innerWidth - CARD_WIDTH - 8
      ));
      cardStyle = { left, top: targetRect.bottom + 14 };
      // Arrow pointing up → toward the button
      const arrowLeft = Math.min(
        Math.max(16, targetRect.left + targetRect.width / 2 - (cardStyle.left ?? 0) - 10),
        CARD_WIDTH - 30
      );
      arrowEl = (
        <Box sx={{
          position: 'absolute',
          bottom: '100%',
          left: arrowLeft,
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: '12px solid #fff',
          filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.08))',
        }} />
      );
    }
  } else if (!hasTarget) {
    // No target: centered in the main content area (left of sidebar)
    cardStyle = {
      right: SIDEBAR_WIDTH + 20,
      top: '50%',
      transform: 'translateY(-50%)',
    };
  }

  // Overlay z-index: below sidebar (1199) for sidebar steps, above everything for centereed steps
  const overlayZIndex = hasTarget && tooltipSide === 'left' ? 1199 : 1300;
  const cardZIndex = overlayZIndex + 100;

  return (
    <>
      {/* Dark overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.55)',
          zIndex: overlayZIndex,
          transition: 'background 0.3s',
        }}
      />

      {/* Tooltip / guide card */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: cardZIndex,
          width: CARD_WIDTH,
          bgcolor: '#fff',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          direction: 'rtl',
          overflow: 'visible',
          ...cardStyle,
        }}
      >
        {arrowEl}

        {/* Header */}
        <Box sx={{
          bgcolor: PRIMARY,
          px: 2,
          py: 1.2,
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
            دليل البدء السريع
          </Typography>
          <IconButton size="small" onClick={dismiss} sx={{ color: 'rgba(255,255,255,0.85)', p: 0.3 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Typography fontWeight={700} fontSize={15} mb={1} color="text.primary">
            {currentStep.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.8} fontSize={13}>
            {currentStep.description}
          </Typography>
        </Box>

        {/* Stepper dots */}
        <MobileStepper
          steps={totalSteps}
          position="static"
          activeStep={stepIndex}
          sx={{
            justifyContent: 'center',
            bgcolor: 'transparent',
            px: 2,
            pt: 0.5,
            pb: 0,
            '& .MuiMobileStepper-dot': { mx: 0.3, width: 6, height: 6 },
            '& .MuiMobileStepper-dotActive': { bgcolor: PRIMARY },
          }}
          nextButton={null}
          backButton={null}
        />

        {/* Buttons row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 2 }}>
          {/* Skip */}
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              cursor: 'pointer',
              '&:hover': { color: 'text.secondary' },
              visibility: isLast ? 'hidden' : 'visible',
            }}
            onClick={dismiss}
          >
            تخطي
          </Typography>

          {/* Prev + Next */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isFirst && (
              <Button
                size="small"
                variant="outlined"
                onClick={prevStep}
                sx={{ borderRadius: 2, borderColor: '#ddd', color: 'text.secondary', minWidth: 72, fontSize: 12 }}
              >
                السابق
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={nextStep}
              endIcon={!isLast ? <ArrowBackIcon sx={{ fontSize: '14px !important' }} /> : null}
              sx={{
                borderRadius: 2,
                bgcolor: PRIMARY,
                minWidth: 72,
                fontSize: 12,
                '&:hover': { bgcolor: '#3b3a9e' },
              }}
            >
              {isLast ? 'إنهاء' : 'التالي'}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default StartGuide;
